import { Typography } from '@bowtie-ins/bowkit';
import {
  BLUSH,
  BOWTIE_BLUE,
  BOWTIE_PINK,
  GRAPHITE,
  INK,
  ROCK,
  STONE,
  WHITE,
} from '@bowtie-ins/bowkit/palette';
import { GtmEvent, dataLayer } from '@bowtie-web/common';
import { Box, ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { startCase } from 'lodash';
import { KeyboardEvent, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useEffectOnce } from 'react-use';
import { INSURED_TARGETS, InsuredTarget } from '../../constants';

type Props = {
  value: InsuredTarget;
  onChange: (value: InsuredTarget) => void;
};

const useStyles = makeStyles(theme => ({
  options: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  option: {
    minHeight: theme.spacing(7.5),
    borderRadius: theme.shape.borderRadius * 3,
    border: `solid 1px ${STONE}`,
    backgroundColor: WHITE,
    color: GRAPHITE,
    '&:hover': {
      borderColor: ROCK,
    },
  },
  focusVisible: {
    outline: `solid 2px ${BOWTIE_BLUE}`,
    outlineOffset: 2,
  },
  selected: {
    borderColor: BOWTIE_PINK,
    backgroundColor: BLUSH,
    color: INK,
    '&:hover': {
      borderColor: BOWTIE_PINK,
    },
  },
}));

/** Lets a signed-in visitor choose whether the quote covers themselves or their family. */
const InsuredTargetToggle = ({ value, onChange }: Props) => {
  const classes = useStyles();
  const optionRefs = useRef<Partial<Record<InsuredTarget, HTMLButtonElement>>>(
    {},
  );

  // Logs the value shown on mount only, including a pre-selected one.
  useEffectOnce(() => {
    dataLayer.push({
      event: GtmEvent.QuotationInsuredTargetShown,
      insured_target: value,
    });
  });

  const handleClick = (option: InsuredTarget) => {
    if (option === value) {
      return;
    }
    onChange(option);
    dataLayer.push({
      event: GtmEvent.QuotationInsuredTargetSelected,
      insured_target: option,
    });
  };

  // Arrow keys move the selection, as in a native radio group.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!event.key.startsWith('Arrow')) {
      return;
    }
    event.preventDefault();
    const next = INSURED_TARGETS.find(option => option !== value) ?? value;
    handleClick(next);
    optionRefs.current[next]?.focus();
  };

  return (
    <Box>
      <Typography variant="body-l" id="insured-target-label">
        <FormattedMessage id="insured_target.label" />
      </Typography>
      <Box
        role="radiogroup"
        aria-labelledby="insured-target-label"
        className={classes.options}
        onKeyDown={handleKeyDown}
      >
        {INSURED_TARGETS.map(option => {
          const isSelected = option === value;
          return (
            <ButtonBase
              key={option}
              ref={(element: HTMLButtonElement | null) => {
                optionRefs.current[option] = element ?? undefined;
              }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              disableRipple
              focusVisibleClassName={classes.focusVisible}
              className={clsx(classes.option, {
                [classes.selected]: isSelected,
              })}
              onClick={() => handleClick(option)}
              data-ui-component="Button"
              data-ui-variant="Insured Target"
              data-ui-action={startCase(option)}
            >
              <Typography as="span" variant="title-m" fontWeight="bold">
                <FormattedMessage id={`insured_target.${option}`} />
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default InsuredTargetToggle;
